import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Language, Utils } from "../../../utils";
import { user as userActions } from "../../../actions";
import { Button, InformationTooltip, ErrorIcon } from "../../../components";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Checkbox, InputLabel } from "@material-ui/core";
import makeClasses from "../styles";
import clsx from "clsx";

const StepEight = ({ history, nextStep, prevStep, traceId, stepOneData }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));

  const i18n = Language(languageTexts);

  const [positions, setPositions] = useState([]);
  const [inputCi, setInputCi] = useState("");
  const [hasCiError, setHasCiError] = useState(false);
  const [inputPosition, setInputPosition] = useState(null);
  const [isPep, setIsPep] = useState(false);

  const canMoveToNext = inputCi.trim().length > 0 && !hasCiError && inputPosition !== null;

  useEffect(() => {
    dispatch(
      userActions.getPositions((positions) => {
        setPositions(positions);
      }),
    );
  }, [dispatch]);

  const _getOptionLabel = (option) => {
    const found = positions.find((p) => p.id === option);
    return found ? found.name : "";
  };

  const handleCiChange = (e) => {
    const value = e.target.value;
    if (value !== "" && !/^\d+$/.test(value)) return;
    setInputCi(value);
    setHasCiError(value.length > 0 && !Utils.inputs.ci.regex.test(value.trim()));
  };

  const handleNext = () => {
    if (!Utils.inputs.ci.regex.test(inputCi.trim())) {
      setHasCiError(true);
      return;
    }

    dispatch(
      userActions.merchantRepresentativeData(
        traceId,
        {
          countryISO: "UY",
          documentTypeKey: "CI",
          document: inputCi.trim(),
          positionId: inputPosition,
          isPoliticallyExposed: isPep,
        },
        [],
        (result) => {
          if (result === "ok") {
            nextStep();
          }
        },
      ),
    );
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={classes.formTitleContainer}>
          <div className={classes.formTitle}>
            {i18n.get("LegalEntityRegistration.StepFormTitle")}
          </div>
          <div className={classes.formText}>
            {i18n.get("LegalEntityRegistration.Step6FormText")}
          </div>
        </div>
        <form layout="vertical" onSubmit={handleNext} className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputCiLabel")}
              </InputLabel>
              <TextField
                error={hasCiError}
                placeholder={i18n.get("LegalEntityRegistration.InputCiTitle")}
                className={classes.formInput}
                onChange={handleCiChange}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputCi}
                inputProps={{ inputMode: "numeric" }}
              />
              {hasCiError && (
                <div className={classes.errorMessageContainer}>
                  <ErrorIcon />
                  <div className={classes.errorMessage}>
                    {i18n.get("LegalEntityRegistration.RegisterInvalidCi")}
                  </div>
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputPositionTitle")}
              </InputLabel>
              <Autocomplete
                options={positions.map((p) => p.id)}
                getOptionLabel={_getOptionLabel}
                onChange={(e, newValue) => setInputPosition(newValue)}
                classes={{ inputRoot: classes.formInputSelect }}
                value={inputPosition}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get(
                      "LegalEntityRegistration.InputPositionTitle",
                    )}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <div
                className={classes.termsContainer}
                style={{ justifyContent: "flex-start" }}>
                <Checkbox
                  classes={{ root: classes.checkboxRoot }}
                  checked={isPep}
                  onChange={(e) => setIsPep(e.target.checked)}
                />
                <span className={classes.checkboxLabel}>
                  {i18n.get("LegalEntityRegistration.InputPepLabel")}
                  <InformationTooltip title={i18n.get("LegalEntityRegistration.PepTooltip")} />
                </span>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
      <div className={classes.termsWrapper}>
        <div
          className={clsx(
            classes.formSubmitContainer,
            classes.formSubmitContainerWithOneButton,
            classes.formSubmitContainerWithNoMargin,
          )}>
          <Button
            className={classes.formSubmitButton}
            disabled={loading || !canMoveToNext}
            onClick={handleNext}>
            {i18n.get("LegalEntityRegistration.NextButton")}
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};

StepEight.id = "com.Handy.LegalEntityRegistration.StepEight";
export default StepEight;
